import { Plus, Swords } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { useCreateRoom } from '../../hooks/useCreateRoom'
import { useFetch } from '../../hooks/useFetch'
import { BOSS_THEME } from '../../lib/bossTheme'
import type { SnapshotOf } from '../../store/duel'
import { useSession } from '../../store/session'
import { BossHero } from '../BossHero'
import { BossStartPicker, loadHumanFirst } from '../BossStartPicker'
import { BottomNav, bossTab, joinTab, menuTab, sheetTab } from '../BottomNav'
import { BottomSheet } from '../BottomSheet'
import { CreateRoomPanel } from '../CreateRoomPanel'
import { JoinCodeForm } from '../JoinCodeForm'
import { PlayingAs } from '../PlayingAs'
import { ResumeCard } from '../ResumeCard'
import { StatChips } from '../StatChips'
import { Button, Card } from '../ui'
import { DuelSheets } from './DuelSheets'
import type { DuelPageProps, DuelSettingsBase } from './defs'

type Sheet = 'create' | 'join' | 'menu' | null

/** A duel's home: who you are, create/join a room, and the boss fight card. */
export function DuelHome<Snap extends SnapshotOf<S>, S extends DuelSettingsBase>({
  def,
}: DuelPageProps<Snap, S>) {
  const me = useSession((s) => s.me)
  const load = useSession((s) => s.load)
  const navigate = useNavigate()
  // Re-fetch on every visit so "resume" reflects rooms left or expired since app load.
  useEffect(() => {
    void load()
  }, [load])
  const [settings, setSettings] = useState<S>(def.settings.load)
  const [sheet, setSheet] = useState<Sheet>(null)
  const [humanFirst, setHumanFirst] = useState(() => loadHumanFirst(def.id))
  const closeSheet = () => setSheet(null)
  const theme = BOSS_THEME[def.id]

  const { data: cpuStats } = useFetch(def.api.cpuStats)
  const { create, creating, error: createError } = useCreateRoom(def)
  const createRoom = (mode: 'pvp' | 'cpu' = 'pvp') => create(mode, { settings, humanFirst })

  const activeRoom = me?.activeRooms[def.id] ?? null
  const createPanel = (
    <CreateRoomPanel
      Form={def.settings.Form}
      settings={settings}
      onChange={setSettings}
      onCreate={() => createRoom()}
      creating={creating}
      disabled={false}
      error={createError}
    />
  )

  return (
    <div className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center gap-5 py-6">
      {activeRoom && <ResumeCard game={def.id} code={activeRoom} />}

      <Card className="flex flex-col gap-4">
        <PlayingAs />
        {/* Phones reach the settings through the Create tab below. */}
        <div className="hidden lg:block">{createPanel}</div>
      </Card>

      <BossHero
        game={def.id}
        bot={def.bot}
        eyebrow={def.boss.eyebrow}
        title={def.boss.title}
        subtitle={def.bot.nickname}
      >
        <div className="mt-4">
          <BossStartPicker
            bot={def.bot}
            humanFirst={humanFirst}
            onChange={setHumanFirst}
            note={def.boss.startNote(cpuStats?.rules.edgeOpening ?? false)}
          />
        </div>
        <StatChips
          className="mt-2"
          items={[
            { label: 'Attempts', value: cpuStats?.totals.games },
            { label: 'Draws', value: cpuStats?.totals.draws },
            { label: 'Gave up', value: cpuStats?.totals.quits },
          ]}
        />
        <Button
          variant="arcade"
          tone={theme.arcade}
          className="mt-4 w-full"
          onClick={() => createRoom('cpu')}
          disabled={creating}
        >
          <Swords className="size-5" aria-hidden /> Challenge
        </Button>
        <Link
          to={`${def.home}/wall`}
          className={`mt-3 block text-center text-sm hover:text-white hover:underline ${theme.sub}`}
        >
          See who tried
        </Link>
      </BossHero>

      <Card className="hidden lg:block">
        <JoinCodeForm
          id="join-code"
          label="Have a code?"
          onJoin={(code) => navigate(`${def.roomPath}/${code}`)}
        />
      </Card>

      <BottomNav
        tabs={[
          sheetTab('create', 'Create', <Plus className="size-5" aria-hidden />, sheet, setSheet),
          bossTab(def.id, def.bot, def.bot.nickname, () => void createRoom('cpu'), creating),
          joinTab(sheet, setSheet),
          menuTab(sheet, setSheet),
        ]}
      />
      <BottomSheet open={sheet === 'create'} onClose={closeSheet} title="Create a room">
        {createPanel}
      </BottomSheet>
      <DuelSheets def={def} sheet={sheet} onClose={closeSheet} joinId="join-code-sheet" />
    </div>
  )
}
